<script setup>
import { ref, watch, nextTick, computed } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import DefaultLayout from '@/Layouts/DefaultLayout.vue';
import ArrowButton from '@/Components/ArrowButton.vue';
import CallToAction from '@/Components/CallToAction.vue';
import Marquee from '@/Components/Marquee.vue';
import SawIcon from '@assets/icons/saw.svg?url';
import ArrowDown from '@assets/icons/arrow_down.svg'

defineOptions({
    layout: DefaultLayout,
});

defineProps({
    pageContent: {
        type: Object,
        default: () => ({}),
    },
    materials: {
        type: Object,
        default: () => ({}),
    },
});

const materialProperties = [
    'grade',
    'standard_designation',
    'technical_designation',
    'alternative_designation',
    'tensile_strength',
    'welding',
    'machinability',
    'formability',
    'corrosion_resistance',
    'hardenability',
    'working_hardness',
    'coating',
    'anodizing',
];

const params = computed(() => new URLSearchParams(location.search));

if (params.value.get('material')) {
    router.replace({
        url: location.pathname +'#'+ params.value.get('material'),
        preserveState: false,
    });
}

const material = ref(location.hash.replace('#', ''));

watch(material, async (id) => {
    if (!id) {
        return;
    }

    history.replaceState(null, '', '#'+ id);

    await nextTick();
    const element = document.getElementById(id);
    if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
});

const scrollIntoView = (id) => {
    material.value = id;
}
</script>

<template>
    <Head :title="pageContent.title">
        <meta property="og:title" :content="$t('meta.wiki.title')" />
        <meta property="og:image" content="/assets/og_images/social-image.jpg" />
        <meta property="og:description" :content="$t('meta.wiki.description')" />
        <meta name="description" :content="$t('meta.wiki.description')" />
    </Head>

    <section class="-mt-16">
        <h1
            class="scrolling-headline"
        >
            <Marquee>
                <div class="flex">
                    <div>Stahl</div>
                    <div aria-hidden="true" class="font-outline-primary text-white">Wiki</div>
                </div>
            </Marquee>
        </h1>
    </section>

    <div class="page-container">

        <hr class="my-16">

        <section class="my-16">
            <div class="flex max-md:flex-col md:items-start gap-8">
                <div
                    class="flex-1 flex items-center gap-2 font-mono text-primary text-sm"
                    v-html="pageContent.disclaimer"
                ></div>
                <div class="flex-1 basis-1/3 flex sm:flex-wrap gap-2 min-h-12 sm:min-h-auto overflow-x-scroll sm:overflow-x-auto">
                    <button
                        v-for="baseMaterial in materials"
                        :key="baseMaterial.id"
                        @click="scrollIntoView(baseMaterial.id)"
                        class="btn btn-sm btn-outline btn-primary border-2"
                    >
                        {{ baseMaterial.title }}
                    </button>
                </div>
            </div>
        </section>

        <template
            v-for="baseMaterial, index in materials"
            :key="baseMaterial.id"
        >
            <hr v-if="index" class="my-16">

            <section :id="baseMaterial.id">
                <div class="flex max-md:flex-col md:items-start gap-8">
                    <div class="flex-1 flex items-center gap-2 heading-5">
                        <img
                            :src="baseMaterial.icon"
                            class="shrink-0 w-14 h-14 motion-safe:animate-spin"
                            :style="{ 'mask': 'url('+ SawIcon +')', 'animation-duration': '5s', 'mask-position': 'center', 'mask-size': '92%', 'mask-repeat': 'no-repeat' }"
                            :alt="baseMaterial.title + ' Saw Icon'"
                        />
                        {{ baseMaterial.title }}
                    </div>
                    <div class="flex-1 basis-1/3">
                        <div
                            v-for="entry in baseMaterial.allChildren"
                            :key="entry.id"
                            :id="entry.id"
                            class="collapse text-accent hover:text-primary transition-colors duration-300"
                        >
                            <input
                                type="radio"
                                name="material"
                                v-model="material"
                                :value="entry.id"
                                class="peer"
                            >
                            <div class="collapse-title flex justify-between items-center peer-checked:text-primary">
                                <h3>
                                    {{ entry.title }}
                                    ({{ entry.description }})
                                </h3>

                                <span class="transform transition-transform duration-300" :class="{'rotate-180': material === entry.id}">
                                    <ArrowDown class="w-8 h-8" />
                                </span>
                            </div>
                            <div class="collapse-content !max-w-7xl !pb-0">

                                <div class="card card-body block xl:columns-2 gap-4 mb-6 space-y-2 bg-base-200/30">
                                    <div>
                                        {{ $t('material.group') }}:
                                        <span class="font-medium font-mono text-primary">
                                            {{ baseMaterial.title }}
                                        </span>
                                    </div>
                                    <div
                                        v-for="property in materialProperties"
                                        :key="property"
                                    >
                                        {{ $t(`material.${property}`) }}:
                                        <span class="font-medium font-mono text-primary">
                                            {{ entry[property] }}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    v-if="entry.brief_description"
                                    class="mb-6 space-y-2"
                                >
                                    <div
                                        class="font-sans heading-7"
                                        v-html="pageContent.material_description"
                                    ></div>
                                    <div>
                                        {{ entry.brief_description }}
                                    </div>
                                </div>

                                <div
                                    v-if="entry.technical_properties"
                                    class="mb-6 space-y-2"
                                >
                                    <div
                                        class="font-sans heading-7"
                                        v-html="pageContent.technical_properties"
                                    ></div>
                                    <div>
                                        {{ entry.technical_properties }}
                                    </div>
                                </div>

                                <div
                                    v-if="entry.intended_use"
                                    class="mb-6 space-y-2"
                                >
                                    <div
                                        class="font-sans heading-7"
                                        v-html="pageContent.intended_use"
                                    ></div>
                                    <div>
                                        {{ entry.intended_use }}
                                    </div>
                                </div>

                                <div
                                    v-if="entry.examples_of_use"
                                    class="mb-6 space-y-2"
                                >
                                    <div
                                        class="font-sans heading-7"
                                        v-html="pageContent.examples_of_use"
                                    ></div>
                                    <div>
                                        {{ entry.examples_of_use }}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </template>

        <CallToAction class="mt-16">
            <p
                class="two-tone"
                v-html="$page.props.globals.cta_text"
            ></p>
            <ArrowButton :href="route('shop.index')" class="my-2">
                <span v-html="$page.props.globals.cta_button"></span>
            </ArrowButton>
        </CallToAction>

    </div>
</template>
